/**
 * The credit balance chip (mission 6 WP05 T032; FR-004, B.3, NFR-005; research
 * §1.7; style guide §7.4; post-tasks P9).
 *
 * Shows `balance` in mono with " credits left" and links to Settings › Billing.
 * `reserved` is never shown: the API's balance already excludes the
 * reservation, so the chip equals the estimate's "Balance before".
 *
 * One read per navigation: the query never goes stale on its own
 * (`creditsQuery`, `staleTime: Infinity`, no focus refetch), and the chip
 * invalidates it only when the pathname changes. The ref starts at the first
 * pathname, so mount reads once, StrictMode's double effect reads nothing more,
 * and a search-only change (`?rule=`) reads nothing. One owner per credit event
 * (P9): after a start, this pathname change is the only trigger; cancel and
 * terminal transitions belong to RunPage, so the chip adds none of its own.
 *
 * Loading is a skeleton. An error renders register 35's `creditsUnavailable`
 * as a caption in navy-600, so the missing balance is never a silent gap and
 * no balance is invented (mission 8 WP10 T053; FR-020 A-L5); the next
 * navigation retries with its one read.
 *
 * Mission 9 WP14 T086 (style guide §7.4, §11; FR-015; B.1, B.5; squad U13, U26), from the same read:
 * - dunning (`billing.state` is `dunning` or `read_only`): `error-50`/`error-700`, led by `chipDunning`;
 *   zero keeps the same error pair;
 * - soft cap (`first_cycle && forgiveness_used > 0`): `warning-100`/`warning-800`;
 * - split: "N credits left · M included" (`chipIncluded`) when the included credits are below the balance;
 * - hidden while `pending_subscription`: AuthGate sends the tenant to subscribe, and there is no balance yet.
 * An absent `billing` member reads as active (U17).
 */
import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'

import { cn } from '@/components/ui/cn'
import { Skeleton } from '@/components/ui/skeleton'
import { count, Mono } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { creditsQuery, invalidateCredits } from '@/lib/queries'

export function CreditChip() {
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const lastPathname = useRef(pathname)

  useEffect(() => {
    if (lastPathname.current === pathname) return
    lastPathname.current = pathname
    void invalidateCredits(queryClient)
  }, [pathname, queryClient])

  const { data, isPending, isError } = useQuery(creditsQuery())

  if (isPending) return <Skeleton className="h-7 w-28 rounded-full" />
  if (isError) return <span className="text-caption text-navy-600">{copyText('creditsUnavailable')}</span>

  const billing = data.billing
  if (billing?.state === 'pending_subscription') return null
  const dunning = billing?.state === 'dunning' || billing?.state === 'read_only'
  const tone: Tone = dunning || data.balance === 0 ? 'error' : data.first_cycle === true && (data.forgiveness_used ?? 0) > 0 ? 'warning' : 'rest'
  const included = billing !== undefined && billing.included_remaining < data.balance ? billing.included_remaining : null
  return (
    <Link
      to="/app/settings/billing"
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-full border px-3 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]',
        TONES[tone].chip,
      )}
    >
      {dunning ? (
        <>
          <span className={cn('text-caption font-medium', TONES.error.label)}>{copyText('chipDunning')}</span>{' '}
          <span className={cn('text-caption', TONES.error.label)}>·</span>{' '}
        </>
      ) : null}
      <Mono className={cn('text-caption font-medium', TONES[tone].number)}>{count(data.balance)}</Mono>{' '}
      {/* The space is a text node so the accessible name reads "N credits left"; flex drops it visually. */}
      {/* mission 6 WP13: handoff from WP05: §7.4's label in the singular at 1, never "1 credits left". */}
      <span className={cn('text-caption', TONES[tone].label)}>{data.balance === 1 ? 'credit left' : 'credits left'}</span>
      {included !== null ? (
        <>
          {' '}
          <span className={cn('text-caption', TONES[tone].label)}>·</span>{' '}
          <span className={cn('text-caption', TONES[tone].label)}>{PROVISIONAL_COPY.chipIncluded.render({ n: included })}</span>
        </>
      ) : null}
    </Link>
  )
}

type Tone = 'rest' | 'warning' | 'error'

/** §7.4's pairs (§11 contrast): resting navy, soft cap warning-100/800, zero and dunning error-50/700. */
const TONES: Record<Tone, { chip: string; number: string; label: string }> = {
  rest: { chip: 'border-navy-300 bg-navy-100', number: 'text-navy-950', label: 'text-navy-600' },
  warning: {
    chip: 'border-[var(--color-warning-200)] bg-[var(--color-warning-100)] text-[var(--color-warning-800)]',
    number: 'text-[var(--color-warning-800)]',
    label: 'text-[var(--color-warning-800)]',
  },
  error: {
    chip: 'border-[var(--color-error-200)] bg-[var(--color-error-50)] text-[var(--color-error-700)]',
    number: 'text-[var(--color-error-700)]',
    label: 'text-[var(--color-error-700)]',
  },
}
